import {
  gachaEvmBuybackInputSchema,
  gachaEvmBuybackQuoteSchema,
  validateGachaEvmBuybackQuote,
} from "@anaconda/core/gacha/evm-buyback"
import {
  type GachaEnvironment,
  getGachaEvmChains,
} from "@anaconda/core/gacha/evm-chains"
import { wrap } from "@reatom/core"
import { type QueryClient, queryOptions } from "@tanstack/react-query"
import { z } from "zod"
import { type GachaApiOptions, getGachaApiUrl } from "./gacha-api"
import type { GachaEvmBuybackOffer } from "./gacha-evm-buyback"
import type { GachaEvmCardIdentity } from "./portfolio/gacha-evm-portfolio"

export class GachaBuybackQuoteRetryError extends Error {
  constructor(
    message: string,
    readonly retryAfterMs = 1000,
  ) {
    super(message)
  }
}

function expiresAt(offer: GachaEvmBuybackOffer) {
  return Math.min(offer.quote.deadline * 1000, offer.eligibilityUntil)
}

export function createGachaEvmBuybackPriceQueryOptions(
  queryClient: QueryClient,
  params: {
    environment: GachaEnvironment | undefined
    identity: GachaEvmCardIdentity
    walletAddress: string
    paymentToken?: string
    eligibilityUntil?: number
    retryTransient?: boolean
    apiOptions: GachaApiOptions
  },
) {
  const {
    environment,
    identity,
    walletAddress,
    paymentToken,
    eligibilityUntil,
    retryTransient = false,
    apiOptions,
  } = params
  const queryKey = [
    "gacha-evm-buyback-price",
    apiOptions.bffBaseUrl?.replace(/\/$/, ""),
    apiOptions.collectorCryptGachaBaseUrl?.replace(/\/$/, ""),
    environment,
    identity.chainId,
    identity.contract.toLowerCase(),
    identity.tokenId,
    walletAddress.toLowerCase(),
    paymentToken?.toLowerCase(),
  ]
  return queryOptions({
    queryKey,
    queryFn: wrap(async ({ signal }: { signal: AbortSignal }) => {
      signal.throwIfAborted()
      if (!environment) throw new Error("Buyback environment unavailable")
      if (
        eligibilityUntil === undefined ||
        !Number.isFinite(eligibilityUntil) ||
        eligibilityUntil <= Date.now()
      )
        throw new Error("This card is not eligible for buyback.")
      const previous = queryClient.getQueryData<GachaEvmBuybackOffer>(queryKey)
      const input = gachaEvmBuybackInputSchema.parse({
        playerAddress: walletAddress,
        evmTokenId: identity.tokenId,
        evmContract: identity.contract,
        chainId: identity.chainId,
        // Keep the first verified payment lane even when renewing an expired offer.
        paymentToken: paymentToken ?? previous?.quote.paymentToken,
      })
      const chain = getGachaEvmChains(environment).find(
        (chain) => chain.chainId === input.chainId,
      )
      if (
        !chain ||
        chain.cardContract.toLowerCase() !== input.evmContract.toLowerCase()
      )
        throw new Error("Buyback card or chain mismatch")
      if (previous && expiresAt(previous) > Date.now()) {
        const candidate = gachaEvmBuybackQuoteSchema.parse(previous.quote)
        validateGachaEvmBuybackQuote(environment, input, candidate)
        return { quote: candidate, eligibilityUntil }
      }
      const requestSignal = AbortSignal.any([
        signal,
        AbortSignal.timeout(15000),
      ])
      const response = await wrap(
        fetch(getGachaApiUrl("/evm/buyback", apiOptions), {
          method: "POST",
          cache: "no-store",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
          signal: requestSignal,
        }),
      )
      const data = await wrap(response.json())
      requestSignal.throwIfAborted()
      if (!response.ok || response.status === 202) {
        const error = z
          .object({
            message: z.string().optional(),
            error: z.string().optional(),
            code: z.string().optional(),
            retryAfterMs: z.number().nonnegative().optional(),
          })
          .safeParse(data)
        const message = error.success
          ? (error.data.message ??
            error.data.error ??
            error.data.code ??
            "Buyback API unavailable; retry later.")
          : "Buyback API unavailable; retry later."
        const detail =
          error.success && error.data.retryAfterMs !== undefined
            ? `${message}. Retry in ${Math.ceil(error.data.retryAfterMs / 1000)}s.`
            : message
        if (
          response.status === 202 ||
          response.status === 429 ||
          response.status >= 500
        )
          throw new GachaBuybackQuoteRetryError(
            detail,
            error.success ? error.data.retryAfterMs : undefined,
          )
        throw new Error(detail)
      }
      const candidate = gachaEvmBuybackQuoteSchema.parse(data)
      validateGachaEvmBuybackQuote(environment, input, candidate)
      if (candidate.deadline * 1000 <= Date.now())
        throw new Error("Buyback quote expired; request a new quote.")
      if (eligibilityUntil <= Date.now())
        throw new Error("This card is not eligible for buyback.")
      return { quote: candidate, eligibilityUntil }
    }),
    staleTime: wrap((query) =>
      query.state.data
        ? Math.max(0, expiresAt(query.state.data) - query.state.dataUpdatedAt)
        : 0,
    ),
    refetchInterval: wrap((query) =>
      query.state.status === "error" || !query.state.data
        ? false
        : Math.max(1000, expiresAt(query.state.data) - Date.now()),
    ),
    retry: retryTransient
      ? (failureCount: number, error: Error) =>
          failureCount < 2 && error instanceof GachaBuybackQuoteRetryError
      : false,
    retryDelay: (_attempt: number, error: Error) =>
      error instanceof GachaBuybackQuoteRetryError
        ? Math.max(1000, error.retryAfterMs)
        : 1000,
    refetchOnWindowFocus: false,
  })
}
